/**
 * Pure encounter (initiative) state transitions. DOM-free, unit-tested.
 *
 * All functions return a NEW EncounterState (or null); none mutate inputs.
 * Persistence + wiring lives in state.ts; this file only computes.
 */

import { buildDeck, compareCards, shuffle } from './deck.js';
import type { Card, EncounterEntry, EncounterState } from './types.js';

/**
 * Deal one card per token id and sort into initiative order.
 * If there are more tokens than the 54-card deck, the deck is recycled
 * (a fresh shuffle) for the overflow; equal cards then tie-break by tokenId
 * so the order stays deterministic.
 */
function dealAndSort(tokenIds: readonly number[], rng?: () => number): EncounterEntry[] {
  let deck = shuffle(buildDeck(), rng);
  let deckIdx = 0;
  const entries: EncounterEntry[] = tokenIds.map((tokenId) => {
    if (deckIdx >= deck.length) {
      deck = shuffle(buildDeck(), rng);
      deckIdx = 0;
    }
    const card = deck[deckIdx++]!;
    return { tokenId, card };
  });
  entries.sort((a, b) => {
    const byCard = compareCards(a.card, b.card);
    if (byCard !== 0) return byCard;
    return a.tokenId - b.tokenId; // deterministic only when deck recycled
  });
  return entries;
}

/** Begin a fresh encounter at round 1 with the active token = first in order. */
export function startEncounter(tokenIds: readonly number[], rng?: () => number): EncounterState {
  const order = dealAndSort(tokenIds, rng);
  return {
    round: 1,
    activeTokenId: order.length > 0 ? order[0]!.tokenId : null,
    order,
  };
}

/** Reshuffle + redeal all current tokens, bump the round, reset active to first. */
export function newRound(
  _state: EncounterState,
  tokenIds: readonly number[],
  rng?: () => number,
): EncounterState {
  const order = dealAndSort(tokenIds, rng);
  return {
    round: _state.round + 1,
    activeTokenId: order.length > 0 ? order[0]!.tokenId : null,
    order,
  };
}

/**
 * Append a token mid-encounter: dealt a card from the cards not already in
 * play, pinned to the end of the order until the next round's reshuffle.
 * No-op if the token is already in the order.
 */
export function addTokenToEncounter(
  state: EncounterState,
  tokenId: number,
  rng?: () => number,
): EncounterState {
  if (state.order.some((e) => e.tokenId === tokenId)) {
    return state;
  }
  const used = new Set(state.order.map((e) => cardKey(e.card)));
  const available = shuffle(buildDeck(), rng).filter((c) => !used.has(cardKey(c)));
  // If somehow exhausted, fall back to a fresh shuffle's first card.
  const card: Card = available[0] ?? shuffle(buildDeck(), rng)[0]!;
  const order = [...state.order, { tokenId, card, pinned: true }];
  return {
    ...state,
    order,
    activeTokenId: state.activeTokenId ?? tokenId,
  };
}

/**
 * Drop tokens no longer present (deleted). If the active token was removed,
 * advance the active pointer to the next still-present entry (or the last,
 * or null when the order becomes empty).
 */
export function reconcile(state: EncounterState, tokenIds: readonly number[]): EncounterState {
  const present = new Set(tokenIds);
  const order = state.order.filter((e) => present.has(e.tokenId));
  if (order.length === state.order.length) {
    return state; // nothing removed
  }
  let activeTokenId = state.activeTokenId;
  if (activeTokenId === null || !present.has(activeTokenId)) {
    // Find where the old active sat, pick the next surviving entry.
    const oldIdx = state.order.findIndex((e) => e.tokenId === state.activeTokenId);
    activeTokenId = null;
    if (order.length > 0) {
      // Scan forward from the old position for the first survivor.
      for (let i = Math.max(0, oldIdx); i < state.order.length; i++) {
        const id = state.order[i]!.tokenId;
        if (present.has(id)) { activeTokenId = id; break; }
      }
      if (activeTokenId === null) activeTokenId = order[order.length - 1]!.tokenId;
    }
  }
  return { ...state, order, activeTokenId };
}

/** Move the active pointer one step toward the end. Stops at the last entry. */
export function next(state: EncounterState): EncounterState {
  const idx = activeIndex(state);
  if (idx < 0 || idx >= state.order.length - 1) return state;
  return { ...state, activeTokenId: state.order[idx + 1]!.tokenId };
}

/** Move the active pointer one step toward the start. Stops at the first entry. */
export function prev(state: EncounterState): EncounterState {
  const idx = activeIndex(state);
  if (idx <= 0) return state;
  return { ...state, activeTokenId: state.order[idx - 1]!.tokenId };
}

/** Index of the active token within `order`, or -1. */
export function activeIndex(state: EncounterState): number {
  if (state.activeTokenId === null) return -1;
  return state.order.findIndex((e) => e.tokenId === state.activeTokenId);
}

function cardKey(card: Card): string {
  return card.kind === 'joker' ? `joker-${card.color}` : `${card.rank}-${card.suit}`;
}
